import { useEffect, useState } from "react";
import { tWithKeyAttr as t } from "../../../i18nHelper";
import { useAgentSipCredentialsDMLMutation } from "../../../services/api/user.api";
import { TAgentSipCredentials } from "../../../services/types/user.types";
import { DEFAULT_SIP_DOMAIN, DEFAULT_SIP_PORT } from "../../../utils/constants";
import Modal from "../../modals/modal/Modal";
import DeleteConfirmation from "../../modals/deleteConfirmation/deleteConfirmation";
import ErrorPopup from "../../modals/errorPopup/errorPopup";
import editIcon from "../../../images/edit_icon.svg";
import formStyles from "../forms.module.css";
import styles from "./sipCredentialsSection.module.css";

/** DML operation codes, matching Admintech/Admin_AgentSipCredentials_DML/{Type}. */
const DML_SELECT = 1;
const DML_UPSERT = 2;
const DML_DELETE = 3;

type TDraft = {
  sipUsername: string;
  sipPassword: string;
  sipDomain: string;
  sipPort: string;
};

/** A brand-new record opens with the provider defaults already filled into the inputs. */
const newDraft = (): TDraft => ({
  sipUsername: "",
  sipPassword: "",
  sipDomain: DEFAULT_SIP_DOMAIN,
  sipPort: String(DEFAULT_SIP_PORT),
});

const toDraft = (creds: TAgentSipCredentials): TDraft => ({
  sipUsername: creds.sipUsername ?? "",
  sipPassword: creds.sipPassword ?? "",
  sipDomain: creds.sipDomain ?? DEFAULT_SIP_DOMAIN,
  sipPort: creds.sipPort != null ? String(creds.sipPort) : String(DEFAULT_SIP_PORT),
});

/** Pulls the server's localized message out of an RTK Query error, if there is one. */
const errorMessage = (error: unknown): string => {
  const data = (error as { data?: { message?: string } } | undefined)?.data;
  return data?.message || t("SOMETHING_WENT_WRONG_TRY_AGAIN_LATER");
};

/**
 * Admin editor for one therapist's Paycall SIP credentials.
 *
 * Saves immediately on each action rather than with the parent user form — the
 * endpoint returns the agent's active rows on every call, so local state is
 * driven straight from the response (same pattern as the specializations block
 * in addNewUserForm).
 */
function SipCredentialsSection({ usersGuid }: { usersGuid: string }) {
  const [agentSipCredentialsDML, { isLoading: isSaving }] =
    useAgentSipCredentialsDMLMutation();

  const [credentials, setCredentials] = useState<TAgentSipCredentials | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState<TDraft>(newDraft);
  const [showPassword, setShowPassword] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    setIsLoaded(false);
    agentSipCredentialsDML({ list: [], type: DML_SELECT, usersGuid }).then((res) => {
      if (cancelled) return;
      if ("data" in res && res.data) setCredentials(res.data[0] ?? null);
      else if ("error" in res) setErrorText(errorMessage(res.error));
      setIsLoaded(true);
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [usersGuid]);

  const openEditor = () => {
    setDraft(credentials ? toDraft(credentials) : newDraft());
    setShowPassword(false);
    setIsEditing(true);
  };

  const handleDraftChange = (field: keyof TDraft, value: string) =>
    setDraft((prev) => ({ ...prev, [field]: value }));

  const handleSave = () => {
    const sipUsername = draft.sipUsername.trim();
    if (!sipUsername) return;

    const parsedPort = parseInt(draft.sipPort, 10);

    agentSipCredentialsDML({
      list: [
        {
          id: credentials?.id ?? 0,
          sipUsername,
          sipPassword: draft.sipPassword,
          // Blank domain/port/WS let the server apply its own defaults.
          sipDomain: draft.sipDomain.trim() || null,
          sipPort: Number.isNaN(parsedPort) ? null : parsedPort,
          // Not editable here; pass the stored override through so an edit never wipes it.
          wsServerUrl: credentials?.wsServerUrl ?? null,
        },
      ],
      type: DML_UPSERT,
      usersGuid,
    }).then((res) => {
      if ("data" in res && res.data) {
        setCredentials(res.data[0] ?? null);
        setIsEditing(false);
        setShowPassword(false);
      } else if ("error" in res) {
        setErrorText(errorMessage(res.error));
      }
    });
  };

  const handleDelete = () => {
    agentSipCredentialsDML({ list: [], type: DML_DELETE, usersGuid }).then((res) => {
      if ("data" in res && res.data) {
        setCredentials(res.data[0] ?? null);
        setIsEditing(false);
        setShowPassword(false);
      } else if ("error" in res) {
        setErrorText(errorMessage(res.error));
      }
    });
  };

  const renderValue = (value?: string | number | null, fallback?: string) =>
    value !== null && value !== undefined && String(value).length > 0 ? (
      <span className={styles.fieldValue}>{value}</span>
    ) : (
      <span className={`${styles.fieldValue} ${styles.fieldValue_muted}`}>
        {fallback ?? "—"}
      </span>
    );

  return (
    <section className={styles.section} data-testid="sip-credentials-section">
      <p className={styles.hint}>{t("SIP_CREDENTIALS_HINT")}</p>

      {!isLoaded ? (
        <div className={styles.loading}>{t("LOADING")}</div>
      ) : isEditing ? (
        <div className={styles.form}>
          {/* Shared form layout so these fields line up with the rest of the user form. */}
          <ul className={`${formStyles.form__list} ${formStyles.form__list_type_fourColumns}`}>
            <li>
              <label htmlFor="sipUsername" className={formStyles.form__label}>
                {t("SIP_USERNAME")} <span className={styles.required}>*</span>
              </label>
              <input
                type="text"
                id="sipUsername"
                data-testid="sipUsername"
                className={`${formStyles.form__input} ${!draft.sipUsername.trim() ? formStyles.form__input_invalid : ""}`}
                value={draft.sipUsername}
                onChange={(e) => handleDraftChange("sipUsername", e.target.value)}
              />
            </li>
            <li>
              <label htmlFor="sipPassword" className={formStyles.form__label}>
                {t("SIP_PASSWORD")}
              </label>
              {/* Reveal button sits inside the input so this cell matches the others' width. */}
              <div className={styles.passwordWrap}>
                <input
                  type={showPassword ? "text" : "password"}
                  id="sipPassword"
                  data-testid="sipPassword"
                  className={`${formStyles.form__input} ${styles.passwordInput}`}
                  value={draft.sipPassword}
                  onChange={(e) => handleDraftChange("sipPassword", e.target.value)}
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  className={styles.revealBtn}
                  onClick={() => setShowPassword((prev) => !prev)}
                  title={showPassword ? t("SIP_HIDE_PASSWORD") : t("SIP_SHOW_PASSWORD")}
                  aria-label={showPassword ? t("SIP_HIDE_PASSWORD") : t("SIP_SHOW_PASSWORD")}
                >
                  {showPassword ? "🙈" : "👁"}
                </button>
              </div>
            </li>
            <li>
              <label htmlFor="sipDomain" className={formStyles.form__label}>
                {t("SIP_DOMAIN")}
              </label>
              <input
                type="text"
                id="sipDomain"
                data-testid="sipDomain"
                className={formStyles.form__input}
                value={draft.sipDomain}
                onChange={(e) => handleDraftChange("sipDomain", e.target.value)}
                placeholder={DEFAULT_SIP_DOMAIN}
              />
            </li>
            <li>
              <label htmlFor="sipPort" className={formStyles.form__label}>
                {t("SIP_PORT")}
              </label>
              <input
                type="number"
                id="sipPort"
                data-testid="sipPort"
                className={formStyles.form__input}
                value={draft.sipPort}
                onChange={(e) => handleDraftChange("sipPort", e.target.value)}
                placeholder={String(DEFAULT_SIP_PORT)}
              />
            </li>
          </ul>

          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.saveBtn}
              data-testid="sip-save-button"
              disabled={isSaving || !draft.sipUsername.trim()}
              onClick={handleSave}
            >
              {t("SAVE")}
            </button>
            <button
              type="button"
              className={styles.cancelBtn}
              disabled={isSaving}
              onClick={() => {
                setIsEditing(false);
                setShowPassword(false);
              }}
            >
              {t("CANCEL")}
            </button>
          </div>
        </div>
      ) : credentials ? (
        <div className={styles.card} data-testid="sip-credentials-card">
          <div className={styles.cardFields}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t("SIP_USERNAME")}</span>
              {renderValue(credentials.sipUsername)}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t("SIP_PASSWORD")}</span>
              <div className={styles.passwordValue}>
                <span className={styles.passwordText}>
                  {credentials.sipPassword
                    ? showPassword
                      ? credentials.sipPassword
                      : "•".repeat(Math.min(credentials.sipPassword.length, 12))
                    : "—"}
                </span>
                {credentials.sipPassword && (
                  <button
                    type="button"
                    className={styles.revealBtn}
                    data-testid="sip-reveal-password"
                    onClick={() => setShowPassword((prev) => !prev)}
                    title={showPassword ? t("SIP_HIDE_PASSWORD") : t("SIP_SHOW_PASSWORD")}
                    aria-label={showPassword ? t("SIP_HIDE_PASSWORD") : t("SIP_SHOW_PASSWORD")}
                  >
                    {showPassword ? "🙈" : "👁"}
                  </button>
                )}
              </div>
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t("SIP_DOMAIN")}</span>
              {renderValue(credentials.sipDomain, DEFAULT_SIP_DOMAIN)}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>{t("SIP_PORT")}</span>
              {renderValue(credentials.sipPort, String(DEFAULT_SIP_PORT))}
            </div>
          </div>

          <div className={styles.cardActions}>
            <button
              type="button"
              className={styles.iconBtn}
              data-testid="sip-edit-button"
              disabled={isSaving}
              onClick={openEditor}
              title={t("EDIT")}
              aria-label={t("EDIT")}
            >
              <img src={editIcon} alt="" />
            </button>
            <button
              type="button"
              className={`${styles.iconBtn} ${styles.iconBtn_danger}`}
              data-testid="sip-delete-button"
              disabled={isSaving}
              onClick={() => setConfirmDelete(true)}
              title={t("DELETE")}
              aria-label={t("DELETE")}
            >
              ✕
            </button>
          </div>
        </div>
      ) : (
        <div className={formStyles.contactTabsEmptyState}>
          <div className={formStyles.contactTabsEmptyStateContent}>
            <div className={formStyles.contactTabsEmptyIcon}>☎</div>
            <p>{t("SIP_NO_CREDENTIALS")}</p>
          </div>
          <button
            type="button"
            className={formStyles.contactTabsEmptyStateButton}
            data-testid="sip-add-button"
            onClick={openEditor}
          >
            <span>+</span>
            {t("SIP_ADD_CREDENTIALS")}
          </button>
        </div>
      )}

      {confirmDelete && (
        <Modal onClose={() => setConfirmDelete(false)}>
          <DeleteConfirmation
            onClose={() => setConfirmDelete(false)}
            onDelete={handleDelete}
            text={t("SIP_DELETE_CONFIRM")}
            warningDetail={t("SIP_DELETE_CONFIRM_WARNING")}
          />
        </Modal>
      )}

      {errorText && (
        <Modal onClose={() => setErrorText(undefined)}>
          <ErrorPopup text={errorText} onClose={() => setErrorText(undefined)} />
        </Modal>
      )}
    </section>
  );
}

export default SipCredentialsSection;
